import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { message } from "antd";
import {
  isAuthPublicRoute,
  isTokenExpiredOrExpiring,
  redirectToAdminLogin,
  refreshAdminAccessToken,
} from "../auth/admin-auth-session";
import { TIMEOUT } from "../utils/config";

// Set the server endpoint based on the environment
var SERVER_ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === "prod"
    ? process.env.REACT_APP_SERVER_PROD_ENDPOINT
    : process.env.REACT_APP_SERVER_DEV_ENDPOINT;

// Create the axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: `${SERVER_ENDPOINT}/${process.env.REACT_APP_API_VERSION}`,
  withCredentials: true,
  timeout: TIMEOUT,
  responseType: "json",
});

// Create the axios instance for uploads
export const axiosUploadInstance = axios.create({
  baseURL: `${SERVER_ENDPOINT}/${process.env.REACT_APP_API_VERSION}`,
  withCredentials: true,
  timeout: TIMEOUT,
  responseType: "json",
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const isFormDataPayload = (data: unknown) =>
  typeof FormData !== "undefined" && data instanceof FormData;

const clearContentTypeHeader = (config: InternalAxiosRequestConfig) => {
  if (!isFormDataPayload(config.data)) return;

  if (typeof config.headers.delete === "function") {
    config.headers.delete("Content-Type");
    return;
  }

  delete (config.headers as Record<string, unknown>)["Content-Type"];
  delete (config.headers as Record<string, unknown>)["content-type"];
};

const isAuthBypassUrl = (url?: string) => {
  if (!url) return false;
  return (
    url.includes("/admin/login") ||
    url.includes("/admin/refresh-token") ||
    url.includes("/admin/reset-password") ||
    url.includes("/admin/common/support-languages")
  );
};

const handleAuthResponseError = async (
  error: AxiosError,
  instance: typeof axiosInstance | typeof axiosUploadInstance
) => {
  const originalRequest = error.config as RetryableRequestConfig | undefined;
  const status = error.response?.status;

  // 401: Unauthorized - try refresh, then logout
  if (status === 401) {
    const isBypass = isAuthBypassUrl(originalRequest?.url) || isAuthPublicRoute();
    const token = localStorage.getItem("token");

    if (originalRequest && !originalRequest._retry && !isBypass && token) {
      originalRequest._retry = true;
      try {
        const accessToken = await refreshAdminAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return instance(originalRequest);
      } catch {
        redirectToAdminLogin();
        return Promise.reject(error);
      }
    }

    if (!isBypass) {
      redirectToAdminLogin();
    }
  }

  // 403: Forbidden - show permission error, do NOT logout
  if (status === 403) {
    // Permission denied - user is authenticated but lacks access
    // Do not redirect to login, just reject the error
    console.error("Permission denied (403):", error.config?.url);
    message.warning({ content: "You do not have permission to perform this action", key: "permission-denied" });
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.request.use(async (config) => {
  let token = localStorage.getItem("token");

  if (token && isTokenExpiredOrExpiring(token)) {
    try {
      token = await refreshAdminAccessToken();
    } catch {
      // Refresh failed - will be caught by response interceptor on 401
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const language = localStorage.getItem("language");
  config.headers["Accept-Language"] = language || "en";

  clearContentTypeHeader(config);

  return config;
});

axiosUploadInstance.interceptors.request.use(async (config) => {
  let token = localStorage.getItem("token");

  if (token && isTokenExpiredOrExpiring(token)) {
    try {
      token = await refreshAdminAccessToken();
    } catch {
      // Refresh failed - will be caught by response interceptor on 401
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const language = localStorage.getItem("language");
  config.headers["Accept-Language"] = language || "en";

  clearContentTypeHeader(config);

  return config;
});

// Add response interceptor to handle 401/403 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => handleAuthResponseError(error, axiosInstance)
);

// Add response interceptor for upload instance
axiosUploadInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => handleAuthResponseError(error, axiosUploadInstance)
);

export default axiosInstance;

